import { FC, useCallback } from 'react';
import {
    Button,
    ButtonLink,
    ButtonStyles,
    TextInput,
    TextInputSizes,
    Tooltip,
    Validation,
    ValidationStatus
} from '@silverfort/sf-components';
import { Controller, ControllerProps, useFormContext, useWatch } from 'react-hook-form';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';

import { UserDrawerFormValues } from '../../../types/forms';
import { useUsers } from '../../../context/UserManagementContext';
import { getUserTokensLimit } from '../../../utils';
import { isUserPermitted } from '../../../../../TowerMenu/TowerMenuUtils';

export interface TokensFooterProps {
    actionTitle: string;
    minimumTokens: number;
    onActionClick: () => void;
    // todo: Remove this flag when 'Disable Tokens' feature is implemented
    isVisible: boolean;
    requiredPermission: string;
}

const getMaxTokensRules = (minimumTokens: number): ControllerProps<UserDrawerFormValues, 'tokenLimit'>['rules'] => ({
    required: 'Max tokens is required',
    min: {
        value: minimumTokens,
        message: `Must be at least ${minimumTokens}`
    }
});

const TokensFooter: FC<TokensFooterProps> = ({
    minimumTokens = 0,
    actionTitle,
    onActionClick,
    requiredPermission,
    isVisible
}) => {
    const { setValue, control } = useFormContext<UserDrawerFormValues>();
    const { systemData, userSession } = useUsers();
    const userTokensLimit = getUserTokensLimit(systemData);
    const currentTokenLimit = useWatch({ control, name: 'tokenLimit' });
    const defaultTokenLimit = Math.max(userTokensLimit, minimumTokens);

    const handleBackToDefaultClick = useCallback(() => {
        setValue('tokenLimit', defaultTokenLimit, {
            shouldDirty: true,
            shouldValidate: true
        });
    }, [userTokensLimit, minimumTokens, setValue, defaultTokenLimit]);

    return (
        <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
                <span className="font-sf text-13 leading-[15.51px] whitespace-nowrap">Max tokens</span>
                <Controller
                    name="tokenLimit"
                    control={control}
                    rules={getMaxTokensRules(minimumTokens)}
                    render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => (
                        <div className="flex flex-col gap-0.5 max-h-8">
                            <TextInput
                                numbersOnly
                                inputSize={TextInputSizes.Small}
                                placeholder="Enter max tokens"
                                value={value}
                                onBlur={onBlur}
                                onChange={onChange}
                            />
                            {error && (
                                <Validation
                                    showIcon={false}
                                    text={error.message || ''}
                                    validationStatus={ValidationStatus.Error}
                                />
                            )}
                        </div>
                    )}
                />
                {Number(currentTokenLimit) !== defaultTokenLimit && (
                    <Tooltip title="Resetting the max token value will revert it to the general configuration in the settings.">
                        <ButtonLink text="Reset" className="text-13" onClick={handleBackToDefaultClick} />
                    </Tooltip>
                )}
            </div>
            {isVisible && (
                <Button
                    text={actionTitle}
                    buttonStyle={ButtonStyles.Tertiary}
                    onClick={onActionClick}
                    disabled={!isUserPermitted(userSession, requiredPermission, ROLES.EDITOR.value)}
                />
            )}
        </div>
    );
};

export default TokensFooter;
